import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

export const CityAutocomplete = ({ value, onChange, onSelect, placeholder = "Rechercher une ville...", className = "" }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/utils/cities?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSuggestions(res.data || []);
      setOpen(true);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange?.(val);
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCities(val), 300);
  };

  const handleSelect = (city) => {
    setQuery(city.nom);
    setSuggestions([]);
    setOpen(false);
    onSelect?.(city);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9 bg-zinc-950 border-zinc-800 text-white"
          data-testid="city-autocomplete-input"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto">
          {suggestions.map((city, i) => (
            <button
              key={`${city.code_commune}-${i}`}
              type="button"
              onClick={() => handleSelect(city)}
              className="w-full text-left px-3 py-2.5 hover:bg-zinc-800 transition-colors flex items-center gap-3 border-b border-zinc-800 last:border-0"
            >
              <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">{city.nom}</p>
                <p className="text-xs text-zinc-500">{city.code_postal} - {city.departement}</p>
              </div>
              {city.population > 0 && (
                <span className="text-[10px] text-zinc-600">{city.population.toLocaleString('fr-FR')} hab.</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
